import { useMemo } from 'react';
import { useIsMounted } from '@toss/react';

import { type ZusToastPosition } from '@zep/toast';

import { useViewport } from '@/hooks/useViewport';
import { useUser } from '@/lib/auth';
import {
  QUIZ_SAVED_LINK_NUDGE_COPY_VALUE,
  QUIZ_SAVED_LINK_NUDGE_QUERY_KEY,
} from '@/shared/features/classroom/consts/classLinkNudge';
import { QUIZ_SAVE_TOAST_POSITION } from '@/shared/features/quiz-folder/utils/quizSaveFeedback';
import { useIsInGoogleClassroom } from '@/shared/googleClassroom';
import { useCanCreateClassroom } from '@/shared/hooks/useCanCreateClassroom';
import { ltiContextManager } from '@/shared/lti';

/** 새 퀴즈가 어디서 생겼나. 상세의 권유 계측(`from`)을 가르는 쿼리 값이 다르다. */
export type QuizSavedFrom = 'build' | 'copy';

/**
 * 「저장 직후 클래스 연결 권유」 정책을 한곳에 둔다. 빌드 저장·플레이 복사·저장 위치 고르기 복사가
 * 보내는 쪽에서, 퀴즈 상세가 받는 쪽에서 같은 값을 쓴다.
 *
 * 값만 돌려준다. 토스트를 띄우고 이동하는 순서는 화면마다 달라(진행 토스트 교체, 창 닫기,
 * 구글 클래스룸 과제 창 쿼리) 각 화면에 둔다.
 */
export function useQuizSavedLinkNudgePolicy() {
  const canCreateClassroom = useCanCreateClassroom();
  const isInGoogleClassroom = useIsInGoogleClassroom();
  const { isMobile } = useViewport();
  const { isInitialized } = useUser();
  const isMounted = useIsMounted();
  // 마운트 전에는 구글 클래스룸 여부가 늘 false, 사용자 초기화 전에는 교사 여부가 늘 false 다.
  // 받는 쪽(상세)이 이 첫 값으로 판단을 끝내면 GC 에서 권유가 뜨거나 새로고침 때 권유가 빠진다.
  const isReady = isMounted && isInitialized;
  // 학생은 클래스를 못 만들고, 구글 클래스룸은 상세에서 과제 만들기 창이 대신 뜬다.
  // LTI 포함 여부는 스쿼드 결정 대기(10/22) — 지금은 빌드 저장과 같게 뺀다.
  const shouldNudge =
    canCreateClassroom && !isInGoogleClassroom && !ltiContextManager.isInLti();

  return useMemo(() => {
    const savedQuizQuery = (from: QuizSavedFrom): Record<string, string> =>
      shouldNudge
        ? {
            [QUIZ_SAVED_LINK_NUDGE_QUERY_KEY]:
              from === 'copy' ? QUIZ_SAVED_LINK_NUDGE_COPY_VALUE : 'true',
          }
        : {};

    return {
      /** 아래 값들을 믿어도 되는가. 화면이 뜨자마자 판단하는 쪽(상세)은 이걸 기다린다. */
      isReady,
      shouldNudge,
      /** 결과 토스트 자리. 모바일 권유는 아래 시트라, 권유가 뜰 때만 위로 올려 시트 버튼을 가리지 않는다. */
      toastPosition: (shouldNudge && isMobile
        ? 'top-center'
        : QUIZ_SAVE_TOAST_POSITION) as ZusToastPosition,
      /** 상세로 넘길 쿼리. 권유를 안 띄우면 비어 있다. */
      savedQuizQuery,
      /** 새 퀴즈 상세 주소. */
      savedQuizPath: (quizsetId: string, from: QuizSavedFrom) => {
        const query = new URLSearchParams(savedQuizQuery(from)).toString();
        return query ? `/quiz/${quizsetId}?${query}` : `/quiz/${quizsetId}`;
      },
    };
  }, [isMobile, isReady, shouldNudge]);
}
